// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Alert,
  Progress
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_240 } from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardHealthcare.json";

import "react-table/react-table.css";

function HealthcareDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiWaitSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor3);
  const intl = useIntl();

  const providerOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "72%" } },
      colors: [c.ReactifyChartColor1],
      dataLabels: { enabled: true, formatter: v => `${v}%` },
      xaxis: {
        categories: data.providerUtilization.map(p => p.name),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const deptWaitOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor3],
      dataLabels: { enabled: true, formatter: v => `${v}m` },
      xaxis: {
        categories: data.departmentWait.map(d => d.dept),
        labels: {
          style: { colors: c.primaryColor },
          rotate: -25,
          rotateAlways: true
        }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v} min`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const noShowOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: 3, curve: "smooth" },
      colors: [c.ReactifyChartColor5],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.noShowTrend.weeks,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}%`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const billingOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.billingMix.map(b => b.label),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "65%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const scheduleColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      { Header: formatMessage({ id: "health.col.time" }), accessor: "time" },
      { Header: formatMessage({ id: "health.col.patient" }), accessor: "patient" },
      { Header: formatMessage({ id: "health.col.provider" }), accessor: "provider" },
      { Header: formatMessage({ id: "health.col.room" }), accessor: "room" },
      { Header: formatMessage({ id: "health.col.type" }), accessor: "type" },
      {
        Header: formatMessage({ id: "health.col.status" }),
        accessor: "status",
        Cell: p => {
          const map = {
            completed: "secondary",
            in_room: "success",
            waiting: "warning",
            checked_in: "info",
            scheduled: "light"
          };
          const col = map[p.value] || "secondary";
          return (
            <Badge
              color={col}
              className={col === "light" ? "text-dark" : ""}
            >
              {p.value.replace("_", " ")}
            </Badge>
          );
        }
      }
    ];
    }, [intl]);

  const queueColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "health.col.ticket" }),
        accessor: "ticket",
        Cell: p => <code className="small">{p.value}</code>
      },
      { Header: formatMessage({ id: "health.col.patient" }), accessor: "patient" },
      {
        Header: formatMessage({ id: "health.col.wait" }),
        accessor: "waitMin",
        Cell: p => `${p.value} min`
      },
      {
        Header: formatMessage({ id: "health.col.priority" }),
        accessor: "priority",
        Cell: p => (
          <Badge color={p.value === "urgent" ? "danger" : "secondary"}>
            {p.value}
          </Badge>
        )
      },
      { Header: formatMessage({ id: "health.col.dept" }), accessor: "dept" }
    ];
    }, [intl]);

  const claimsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "health.col.claim" }),
        accessor: "id",
        Cell: p => <code className="small">{p.value}</code>
      },
      { Header: formatMessage({ id: "health.col.payer" }), accessor: "payer" },
      {
        Header: formatMessage({ id: "health.col.amount" }),
        accessor: "amount",
        Cell: p => `$${addCommas(p.value)}`
      },
      {
        Header: formatMessage({ id: "health.col.age" }),
        accessor: "ageDays",
        Cell: p => `${p.value}d`
      },
      {
        Header: formatMessage({ id: "health.col.status" }),
        accessor: "status",
        Cell: p => <Badge color="info">{p.value}</Badge>
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--healthcare">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.healthcare" />}
              />
              <Separator className="mb-3" />
              <Alert color="warning" className="mb-4">
                <strong>
                  <IntlMessages id="health.disclaimer.title" />
                </strong>{" "}
                <IntlMessages id="health.disclaimer.body" />
              </Alert>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-calendar-check-o text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.appts" />
                  </p>
                  <h4 className="mb-0">{k.appointmentsToday}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-user text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.checkin" />
                  </p>
                  <h4 className="mb-0">{k.checkedIn}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.waitSpark} margin={6}>
                    <SparklinesLine style={kpiWaitSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.wait" />
                  </p>
                  <h4 className="mb-0">{k.avgWaitMin}m</h4>
                  <span className="text-success small">{k.waitDelta}m</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-user-times text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.noshow" />
                  </p>
                  <h4 className="mb-0">{k.noShowPct}%</h4>
                  <span className="text-success small">{k.noShowDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-stethoscope text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.providers" />
                  </p>
                  <h4 className="mb-0">{k.providersOnDuty}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-star text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="health.kpi.csat" />
                  </p>
                  <h4 className="mb-0">{k.satisfaction}</h4>
                  <p className="text-muted small mb-0">
                    <IntlMessages id="health.kpi.revenue" /> $
                    {addCommas(k.revenueDay)}
                  </p>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="7" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.table.schedule" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={6}
                    data={data.todaysSchedule}
                    columns={scheduleColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.table.queue" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.queue}
                    columns={queueColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.chart.providers" />
                  </CardTitle>
                  <Chart
                    options={providerOptions}
                    series={[
                      {
                        name: "Utilization",
                        data: data.providerUtilization.map(p => p.utilPct)
                      }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.chart.dept-wait" />
                  </CardTitle>
                  <Chart
                    options={deptWaitOptions}
                    series={[
                      {
                        name: "Wait",
                        data: data.departmentWait.map(d => d.avgMin)
                      }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.chart.billing" />
                  </CardTitle>
                  <Chart
                    options={billingOptions}
                    series={data.billingMix.map(b => b.value)}
                    type="donut"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.chart.noshow" />
                  </CardTitle>
                  <Chart
                    options={noShowOptions}
                    series={[
                      { name: "No-show %", data: data.noShowTrend.rate }
                    ]}
                    type="area"
                    height={260}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.section.patients-day" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_240}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>
                            <IntlMessages id="health.col.provider" />
                          </th>
                          <th>
                            <IntlMessages id="health.col.patients" />
                          </th>
                          <th>
                            <IntlMessages id="health.col.util" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.providerUtilization.map((p, i) => (
                          <tr key={i}>
                            <td>{p.name}</td>
                            <td>{p.patients}</td>
                            <td>
                              <Progress value={p.utilPct} className="mb-0" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx xxs="12" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="health.table.claims" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.claimsQueue}
                    columns={claimsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default HealthcareDashboard;
